import { PaginatedQueryResult } from '@query/usePaginatedQuery'
import React, { PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'

import { useIsRTL } from '@context/SessionSettingsContext'

import { Truncate } from '@ui/Truncate/Truncate'
import {
  ColumnSizeProps,
  Table,
  TableBody,
  TableHead,
  TableHeadSort,
  TableHeader,
  TableRow,
} from '@ui/v2/Table/Table'
import { TableHeaderTooltip } from '@ui/v2/Table/TableHeaderTooltip'
import { VIEW_MORE_ARROW_COL_KEY } from '@ui/v2/Table/hooks/useTableSearch'

import { useWindowResize } from '@utils/domUtils'
import { getCentralRowIdConditionally } from '@utils/getItemId'

import { NameDto } from '@model/NameDto'

import { EmptyIBClientsTable } from './IBEmptyReportsTable'

type IBReportsTableHeadProps = ColumnSizeProps & {
  align?: 'left' | 'center' | 'right'
  className?: string
  isNumeric?: boolean
  visibleOnMobile?: boolean
  visibleOnDesktop?: boolean
}

export type IBReportsTableSortOption<T = string | null> = NameDto<T> & IBReportsTableHeadProps

interface IBReportsTableProps<T> {
  sortOptions?: IBReportsTableSortOption<string | null | undefined>[]
  renderColumns(item: T, index: number): React.ReactNode[]
  tableQuery: PaginatedQueryResult<T>
  onRowClick?: (item: T, index: number) => void
  className?: string
  rowClassName?: string
  noResults?: React.ReactNode
  animateNewRows?: boolean
  getRowKey?: (item: T, index: number) => React.Key
}

export const IBReportsTable = <T,>(
  props: PropsWithChildren<IBReportsTableProps<T>>
): React.ReactElement<any> => {
  const {
    tableQuery,
    sortOptions = [],
    renderColumns,
    onRowClick,
    className,
    rowClassName,
    noResults,
    animateNewRows,
    getRowKey,
  } = props
  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const isRTL = useIsRTL()
  const { data, pageQuery, setPageQuery, isLoading } = tableQuery
  const emptyState = noResults || (
    <EmptyIBClientsTable
      isAnyFilterActive={Object.entries(pageQuery?.search ?? {}).some(
        ([key, value]) => key !== 'ca_search_TextSearch' && !!value
      )}
    />
  )

  const visibleSortOptions = isMobile
    ? sortOptions.filter(({ visibleOnMobile }) => visibleOnMobile)
    : sortOptions.filter(
        ({ id, visibleOnDesktop = true }) => id !== VIEW_MORE_ARROW_COL_KEY && visibleOnDesktop
      )

  return (
    <Table
      isLoading={isLoading}
      noResults={data.length === 0 && emptyState}
      className={className}
      isLayoutFixed={isMobile}
    >
      <TableHeader>
        <TableRow>
          {visibleSortOptions.map((option, idx) => {
            const { className: headClassName, width, minWidth, maxWidth } = option

            const baseAlign = option.align ?? (option.isNumeric ? 'right' : 'left')
            const align =
              isRTL && baseAlign !== 'center'
                ? baseAlign === 'left'
                  ? 'right'
                  : 'left'
                : baseAlign

            if (option.id === VIEW_MORE_ARROW_COL_KEY) {
              return <TableHead key={option.id} align='center' width={width} />
            }

            const sizeProps = { width, minWidth, maxWidth }

            return option.id ? (
              <TableHeadSort
                id={option.id}
                key={option.id}
                align={align}
                {...sizeProps}
                className={headClassName}
                sort={pageQuery?.sort}
                sortOrder={pageQuery?.sortOrder}
                setSort={(sort, sortOrder) => {
                  setPageQuery?.({ ...pageQuery, sort, sortOrder })
                }}
              >
                <Truncate>{option.name}</Truncate>
                <TableHeaderTooltip
                  headerIds={{
                    FxmVolume: t('IB.RebatesPage.Forex & Metals'),
                    OtherVolume: t('IB.RebatesPage.Other instrument groups'),
                    FxmAmount: t('IB.RebatesPage.Forex & Metals'),
                    OtherAmount: t('IB.RebatesPage.Other instrument groups'),
                  }}
                  targetHeaderId={
                    option.id as 'FxmVolume' | 'OtherVolume' | 'FxmAmount' | 'OtherAmount'
                  }
                />
              </TableHeadSort>
            ) : (
              <TableHead
                align={align}
                {...sizeProps}
                className={headClassName}
                key={`${option.name}-${idx}`}
              >
                <Truncate>{option.name}</Truncate>
              </TableHead>
            )
          })}
        </TableRow>
      </TableHeader>
      <TableBody
        animateNewRows={animateNewRows}
        pageKey={`${tableQuery.meta?.pageIndex ?? 1}:${tableQuery.meta?.pageSize ?? 'default'}`}
      >
        {data.map((x, index) => {
          return (
            <TableRow
              {...getCentralRowIdConditionally(index, data.length, 'ib-reports')}
              key={getRowKey?.(x, index) ?? index}
              className={rowClassName}
              onClick={onRowClick ? () => onRowClick(x, index) : undefined}
            >
              {renderColumns(x, index)}
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
